import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CloudUpload,
  Visibility,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Assessment,
  DataObject,
  FileUpload,
  Analytics,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Scatter,
  ScatterChart,
} from 'recharts';

import { CreditRiskAPI } from '../services/api';
import { DataAnalysis } from '../types';

interface DatasetManagementProps {
  onNotification: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

const DatasetManagement: React.FC<DatasetManagementProps> = ({ onNotification }) => {
  const [datasetInfo, setDatasetInfo] = useState<DataAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [datasetType, setDatasetType] = useState('training');

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  useEffect(() => {
    loadDatasetInfo();
  }, []);

  const loadDatasetInfo = async () => {
    setLoading(true);
    try {
      const response = await CreditRiskAPI.getDatasetInfo();
      if (response.success && response.data) {
        setDatasetInfo(response.data);
      }
    } catch (error) {
      onNotification('Error al cargar información del dataset', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      onNotification('Por favor seleccione un archivo', 'warning');
      return;
    }

    try {
      setUploadProgress(0);
      const response = await CreditRiskAPI.uploadDataset(selectedFile, datasetType);

      if (response.success && response.data) {
        onNotification('Archivo subido exitosamente', 'success');
        setUploadDialog(false);
        setSelectedFile(null);
        setUploadProgress(0);
        loadDatasetInfo(); // Refresh dataset info
      } else {
        onNotification(response.error?.message || 'Error al subir archivo', 'error');
      }
    } catch (error) {
      onNotification('Error al conectar con el servidor', 'error');
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

      if (!allowedTypes.includes(fileExtension)) {
        onNotification('Tipo de archivo no válido. Use CSV o Excel.', 'error');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        onNotification('El archivo es demasiado grande. Máximo 10MB.', 'error');
        return;
      }

      setSelectedFile(file);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const renderDistributionChart = (distribution: any[]) => {
    if (!distribution || distribution.length === 0) return null;

    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={distribution}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="value" fill="#8884d8" />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderTargetDistribution = () => {
    if (!datasetInfo?.dataset_info.target_distribution) return null;

    const data = Object.entries(datasetInfo.dataset_info.target_distribution).map(([key, value]) => ({
      name: key === '1' ? 'Default' : 'No Default',
      value: value as number
    }));

    return (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={({ name, percent = 0 }) => `${name} ${(percent * 100).toFixed(1)}%`}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Gestión de Datos
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Analytics />}
            onClick={loadDatasetInfo}
            disabled={loading}
          >
            Actualizar Análisis
          </Button>
          <Button
            variant="contained"
            startIcon={<FileUpload />}
            onClick={() => setUploadDialog(true)}
          >
            Subir Dataset
          </Button>
        </Box>
      </Box>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Gestione los datasets utilizados para entrenar los modelos y visualice las características de los datos.
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : datasetInfo ? (
        <Grid container spacing={3}>
          {/* Dataset Overview */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Información General del Dataset
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {datasetInfo.dataset_info.total_records.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">Registros Totales</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {datasetInfo.dataset_info.features}
                      </Typography>
                      <Typography variant="caption">Características</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {datasetInfo.missing_values.total_missing.toLocaleString()}
                      </Typography>
                      <Typography variant="caption">Valores Faltantes</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">
                        {datasetInfo.dataset_info.memory_usage}
                      </Typography>
                      <Typography variant="caption">Uso de Memoria</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Target Distribution */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Distribución de la Variable Objetivo
                </Typography>
                {renderTargetDistribution()}
              </CardContent>
            </Card>
          </Grid>

          {/* Missing Values Analysis */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Análisis de Valores Faltantes
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Porcentaje Total de Valores Faltantes
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={datasetInfo.missing_values.percentage_missing}
                    sx={{ height: 10, borderRadius: 5, mb: 1 }}
                    color={datasetInfo.missing_values.percentage_missing > 10 ? 'error' : 'primary'}
                  />
                  <Typography variant="body2">
                    {datasetInfo.missing_values.percentage_missing.toFixed(2)}%
                  </Typography>
                </Box>

                {/* Top features with missing values */}
                <Typography variant="subtitle2" gutterBottom>
                  Características con Más Valores Faltantes
                </Typography>
                <TableContainer sx={{ maxHeight: 200 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Característica</TableCell>
                        <TableCell align="right">Faltantes</TableCell>
                        <TableCell align="right">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(datasetInfo.missing_values.by_feature)
                        .sort(([,a], [,b]) => b.count - a.count)
                        .slice(0, 5)
                        .map(([feature, info]) => (
                          <TableRow key={feature}>
                            <TableCell>{feature}</TableCell>
                            <TableCell align="right">{info.count}</TableCell>
                            <TableCell align="right">{info.percentage.toFixed(1)}%</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Data Types */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Tipos de Datos por Característica
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Característica</TableCell>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Categoría</TableCell>
                        <TableCell>Estadísticas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(datasetInfo.dataset_info.data_types).map(([feature, dataType]) => {
                        const isNumerical = ['int64', 'float64', 'int32', 'float32'].includes(dataType);
                        const stats = isNumerical
                          ? datasetInfo.statistics?.numerical?.[feature]
                          : datasetInfo.statistics?.categorical?.[feature];

                        return (
                          <TableRow key={feature}>
                            <TableCell>{feature}</TableCell>
                            <TableCell>
                              <Chip
                                label={dataType}
                                size="small"
                                color={isNumerical ? 'primary' : 'secondary'}
                              />
                            </TableCell>
                            <TableCell>{isNumerical ? 'Numérica' : 'Categórica'}</TableCell>
                            <TableCell>
                              {stats && (
                                <Typography variant="body2">
                                  {isNumerical
                                    ? `Media: ${(stats as any).mean?.toFixed(2)}, Std: ${(stats as any).std?.toFixed(2)}`
                                    : `Únicos: ${(stats as any).unique}, Frecuente: ${(stats as any).top}`
                                  }
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Grid>

          {/* Feature Distributions */}
          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  Distribuciones de Características ({datasetInfo.distributions.length} gráficos)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={3}>
                  {datasetInfo.distributions.slice(0, 4).map((dist, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>
                            {dist.feature}
                          </Typography>
                          <Chip
                            label={dist.type}
                            size="small"
                            color={dist.type === 'numerical' ? 'primary' : 'secondary'}
                            sx={{ mb: 2 }}
                          />
                          {dist.type === 'numerical' && dist.data.bins && dist.data.values ? (
                            <ResponsiveContainer width="100%" height={250}>
                              <BarChart data={dist.data.bins.map((bin, i) => ({
                                range: `${bin.toFixed(1)}`,
                                count: dist.data.values![i]
                              }))}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="range" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#8884d8" />
                              </BarChart>
                            </ResponsiveContainer>
                          ) : (
                            <ResponsiveContainer width="100%" height={250}>
                              <BarChart data={dist.data.labels?.map((label, i) => ({
                                category: label,
                                count: dist.data.counts![i]
                              })) || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="category" />
                                <YAxis />
                                <Tooltip />
                                <Bar dataKey="count" fill="#82ca9d" />
                              </BarChart>
                            </ResponsiveContainer>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <DataObject sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay datos disponibles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Suba un dataset para comenzar el análisis
          </Typography>
          <Button
            variant="contained"
            startIcon={<FileUpload />}
            onClick={() => setUploadDialog(true)}
          >
            Subir Dataset
          </Button>
        </Box>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onClose={() => setUploadDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Subir Nuevo Dataset
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Formatos soportados: CSV, Excel (.xlsx, .xls). Tamaño máximo: 10MB
            </Alert>

            <TextField
              select
              fullWidth
              label="Tipo de Dataset"
              value={datasetType}
              onChange={(e) => setDatasetType(e.target.value)}
              sx={{ mb: 3 }}
              SelectProps={{ native: true }}
            >
              <option value="training">Entrenamiento</option>
              <option value="validation">Validación</option>
              <option value="test">Prueba</option>
            </TextField>

            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => document.getElementById('file-input')?.click()}
            >
              <input
                id="file-input"
                type="file"
                hidden
                accept=".csv,.xlsx,.xls"
                onChange={handleFileSelect}
              />
              <CloudUpload sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              {selectedFile ? (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    {selectedFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatFileSize(selectedFile.size)}
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" gutterBottom>
                    Haga clic para seleccionar archivo
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    o arrastre y suelte aquí
                  </Typography>
                </Box>
              )}
            </Box>

            {uploadProgress > 0 && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                  Subiendo... {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleFileUpload}
            variant="contained"
            disabled={!selectedFile || uploadProgress > 0}
            startIcon={<CloudUpload />}
          >
            Subir Archivo
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DatasetManagement;